import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, Map, List, X, Loader2, AlertTriangle, Home, Plus, Wifi, Car, Dumbbell, Key } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import Layout from '../components/Layout';
import PropertyCard from '../components/PropertyCard';
import { propertiesApi, savedApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { usePageMetadata } from '../hooks/usePageMetadata';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Expanded amenities list
const AMENITIES = [
  { id: 'WiFi', label: 'WiFi' },
  { id: 'Fast WiFi', label: 'Fast WiFi' },
  { id: 'Kitchen', label: 'Kitchen' },
  { id: 'Parking', label: 'Free Parking' },
  { id: 'Laundry', label: 'Laundry' },
  { id: 'Air Conditioning', label: 'Air Conditioning' },
  { id: 'Heating', label: 'Heating' },
  { id: 'Pool', label: 'Pool' },
  { id: 'Gym', label: 'Gym' },
  { id: 'Pet Friendly', label: 'Pet Friendly' },
  { id: 'Furnished', label: 'Furnished' },
  { id: 'Keyless Entry', label: 'Keyless Entry' },
  { id: 'Dedicated Workspace', label: 'Dedicated Workspace' },
  { id: 'Blackout Curtains', label: 'Blackout Curtains' },
  { id: 'EV Charger', label: 'EV Charger' },
  { id: 'King Size Bed', label: 'King Size Bed' }
];

// Accessibility features
const ACCESSIBILITY_FEATURES = [
  { id: 'Wheelchair Accessible', label: 'Wheelchair Accessible' },
  { id: 'Elevator', label: 'Elevator' },
  { id: 'Close to Public Transportation', label: 'Close to Public Transportation' },
  { id: 'Disabled Parking', label: 'Disabled Parking' }
];

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [publicSettings, setPublicSettings] = useState(null);
  
  // Apply page metadata
  usePageMetadata();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [priceRange, setPriceRange] = useState([500, 5000]);
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedAccessibility, setSelectedAccessibility] = useState([]);
  const [instantBooking, setInstantBooking] = useState(false);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (priceRange[0] > 500 || priceRange[1] < 5000) count++;
    if (propertyType) count++;
    if (bedrooms) count++;
    if (bathrooms) count++;
    if (selectedAmenities.length) count += selectedAmenities.length;
    if (selectedAccessibility.length) count += selectedAccessibility.length;
    if (instantBooking) count++;
    return count;
  }, [priceRange, propertyType, bedrooms, bathrooms, selectedAmenities, selectedAccessibility, instantBooking]);

  // Check if there are demo listings being shown
  const hasDemoListings = properties.some(p => p.is_demo);

  useEffect(() => {
    fetchPublicSettings();
    fetchProperties();
    if (isAuthenticated) {
      fetchSavedProperties();
    }
  }, [isAuthenticated]);

  const fetchPublicSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/public`);
      setPublicSettings(res.data);
    } catch (error) {
      console.error('Error fetching public settings:', error);
    }
  };

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.city = searchQuery;
      if (priceRange[0] > 500) params.min_price = priceRange[0];
      if (priceRange[1] < 5000) params.max_price = priceRange[1];
      if (propertyType) params.property_type = propertyType;
      if (bedrooms) params.bedrooms = parseInt(bedrooms);
      if (bathrooms) params.bathrooms = parseInt(bathrooms);
      if (selectedAmenities.length) params.amenities = selectedAmenities.join(',');
      if (selectedAccessibility.length) params.accessibility = selectedAccessibility.join(',');
      if (instantBooking) params.instant_booking = true;

      const response = await propertiesApi.getAll(params);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedProperties = async () => {
    try {
      const response = await savedApi.getAll();
      setSavedIds(new Set(response.data.map(p => p.property_id)));
    } catch (error) {
      console.error('Error fetching saved:', error);
    }
  };

  const handleSave = async (propertyId) => {
    if (!isAuthenticated) {
      toast.error('Please log in to save properties');
      return;
    }
    try {
      const response = await savedApi.toggle(propertyId);
      if (response.data.saved) {
        setSavedIds(prev => new Set([...prev, propertyId]));
        toast.success('Property saved');
      } else {
        setSavedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(propertyId);
          return newSet;
        });
        toast.success('Property unsaved');
      }
    } catch (error) {
      toast.error('Failed to save property');
    }
  };

  const applyFilters = () => {
    fetchProperties();
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPriceRange([500, 5000]);
    setPropertyType('');
    setBedrooms('');
    setBathrooms('');
    setSelectedAmenities([]);
    setSelectedAccessibility([]);
    setInstantBooking(false);
    setTimeout(() => fetchProperties(), 0);
  };

  // LA center coordinates
  const mapCenter = useMemo(() => [34.0522, -118.2437], []);

  return (
    <Layout showFooter={false}>
      <div className="h-[calc(100vh-64px)] flex flex-col">
        {/* Search Bar */}
        <div className="bg-white border-b border-slate-200 p-4">
          <div className="max-w-7xl mx-auto flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by city, neighborhood, or zip..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchProperties()}
                className="pl-10"
                data-testid="browse-search"
              />
            </div>

            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge className="bg-trust-blue text-white text-xs px-1.5 py-0.5 ml-1">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6 pb-24">
                  {/* Price Range */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-3 block">
                      Price Range: ${priceRange[0]} - ${priceRange[1]}/month
                    </label>
                    <Slider
                      value={priceRange}
                      onValueChange={setPriceRange}
                      min={500}
                      max={5000}
                      step={100}
                      className="mt-2"
                    />
                  </div>

                  {/* Property Type */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Property Type
                    </label>
                    <Select value={propertyType || 'all'} onValueChange={(v) => setPropertyType(v === 'all' ? '' : v)}>
                      <SelectTrigger data-testid="filter-property-type">
                        <SelectValue placeholder="Any type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any type</SelectItem>
                        <SelectItem value="room">Private Room</SelectItem>
                        <SelectItem value="adu">ADU</SelectItem>
                        <SelectItem value="guesthouse">Guesthouse</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Beds & Baths Section */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-3 block">
                      Beds & Baths
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Bedrooms */}
                      <div>
                        <label className="text-xs text-slate-500 mb-1.5 block">Bedrooms</label>
                        <Select value={bedrooms || 'any'} onValueChange={(v) => setBedrooms(v === 'any' ? '' : v)}>
                          <SelectTrigger data-testid="filter-bedrooms">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="1">1+</SelectItem>
                            <SelectItem value="2">2+</SelectItem>
                            <SelectItem value="3">3+</SelectItem>
                            <SelectItem value="4">4+</SelectItem>
                            <SelectItem value="5">5+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Bathrooms */}
                      <div>
                        <label className="text-xs text-slate-500 mb-1.5 block">Bathrooms</label>
                        <Select value={bathrooms || 'any'} onValueChange={(v) => setBathrooms(v === 'any' ? '' : v)}>
                          <SelectTrigger data-testid="filter-bathrooms">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="1">1+</SelectItem>
                            <SelectItem value="2">2+</SelectItem>
                            <SelectItem value="3">3+</SelectItem>
                            <SelectItem value="4">4+</SelectItem>
                            <SelectItem value="5">5+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Booking Options */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-3 block">
                      Booking Options
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="instant"
                          checked={instantBooking}
                          onCheckedChange={setInstantBooking}
                          data-testid="filter-instant-booking"
                        />
                        <label htmlFor="instant" className="text-sm text-slate-600">
                          Instant booking only
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="keyless"
                          checked={selectedAmenities.includes('Keyless Entry')}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedAmenities([...selectedAmenities, 'Keyless Entry']);
                            } else {
                              setSelectedAmenities(selectedAmenities.filter(a => a !== 'Keyless Entry'));
                            }
                          }}
                          data-testid="filter-keyless-entry"
                        />
                        <label htmlFor="keyless" className="text-sm text-slate-600 flex items-center gap-1.5">
                          <Key className="w-3.5 h-3.5" />
                          Keyless entry
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-3 block">
                      Amenities
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {AMENITIES.filter(a => a.id !== 'Keyless Entry').map((amenity) => (
                        <div key={amenity.id} className="flex items-center gap-2">
                          <Checkbox
                            id={amenity.id}
                            checked={selectedAmenities.includes(amenity.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAmenities([...selectedAmenities, amenity.id]);
                              } else {
                                setSelectedAmenities(selectedAmenities.filter(a => a !== amenity.id));
                              }
                            }}
                            data-testid={`filter-amenity-${amenity.id.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <label htmlFor={amenity.id} className="text-sm text-slate-600">
                            {amenity.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Accessibility Features */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-3 block">
                      Accessibility Features
                    </label>
                    <div className="space-y-2">
                      {ACCESSIBILITY_FEATURES.map((feature) => (
                        <div key={feature.id} className="flex items-center gap-2">
                          <Checkbox
                            id={feature.id}
                            checked={selectedAccessibility.includes(feature.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAccessibility([...selectedAccessibility, feature.id]);
                              } else {
                                setSelectedAccessibility(selectedAccessibility.filter(a => a !== feature.id));
                              }
                            }}
                            data-testid={`filter-accessibility-${feature.id.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <label htmlFor={feature.id} className="text-sm text-slate-600">
                            {feature.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons - Fixed at bottom */}
                  <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 flex gap-3 sm:max-w-md sm:left-auto">
                    <Button variant="outline" onClick={clearFilters} className="flex-1" data-testid="clear-filters">
                      Clear all
                    </Button>
                    <Button onClick={applyFilters} className="flex-1 bg-trust-blue hover:bg-trust-blue-hover" data-testid="apply-filters">
                      Apply filters
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <div className="hidden md:flex border rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-trust-blue' : ''}
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-trust-blue' : ''}
                data-testid="view-map"
              >
                <Map className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {/* Demo Content Banner */}
          {hasDemoListings && publicSettings?.demo_content_enabled && (
            <div className="bg-amber-500 text-white px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Demo listings only — these are sample properties, not real inventory.
                </span>
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
            </div>
          ) : viewMode === 'list' ? (
            <div className="h-full overflow-y-auto p-6">
              <div className="max-w-7xl mx-auto">
                <p className="text-slate-600 mb-6">{properties.length} properties in Los Angeles County</p>
                {properties.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-soft-gray rounded-full mb-6">
                      <Home className="w-10 h-10 text-slate-400" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                      No listings yet in Los Angeles County
                    </h2>
                    <p className="text-slate-500 mb-8 max-w-md mx-auto">
                      Be the first to list your property and connect with renters seeking 30+ day stays.
                    </p>
                    <Link to="/register?role=owner">
                      <Button className="bg-coral hover:bg-coral-hover text-white" data-testid="list-space-cta">
                        <Plus className="w-4 h-4 mr-2" />
                        List your space
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {properties.map((property) => (
                      <PropertyCard
                        key={property.property_id}
                        property={property}
                        onSave={handleSave}
                        isSaved={savedIds.has(property.property_id)}
                        showOwner
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex">
              {/* Map */}
              <div className="flex-1 relative">
                <MapContainer
                  center={mapCenter}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {properties.map((property) => (
                    <Marker
                      key={property.property_id}
                      position={[property.lat, property.lng]}
                    >
                      <Popup>
                        <div className="w-48">
                          <img 
                            src={property.photos?.[0] || 'https://via.placeholder.com/200'} 
                            alt={property.title}
                            className="w-full h-24 object-cover rounded mb-2"
                          />
                          <h4 className="font-semibold text-sm">{property.title}</h4>
                          <p className="text-sm text-slate-600">${property.price_per_month}/month</p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>

              {/* Side List */}
              <div className="w-96 border-l border-slate-200 overflow-y-auto p-4 hidden lg:block">
                <p className="text-slate-600 mb-4">{properties.length} properties</p>
                <div className="space-y-4">
                  {properties.map((property) => (
                    <PropertyCard
                      key={property.property_id}
                      property={property}
                      onSave={handleSave}
                      isSaved={savedIds.has(property.property_id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BrowsePage;
